// Plan v7 / Day 24 F6 — JS port of `UrlGenerator::localize()` (PHP).
//
// Translates a URL from any of the 5 supported locales to any other,
// preserving query string, fragment, scheme, and host.
//
// Hub-spoke: if current != tr, first reverse-translate to TR, then forward
// to target. This keeps the mapping pair count at N+M instead of N*M.
//
// Matches behavior of `app/Services/UrlGenerator.php::localize()` exactly.
//
// Usage:
//   localizeUrl('https://cine5.com/tr/film/dovus-kulubu', 'en')
//     => 'https://cine5.com/en/movie/dovus-kulubu'
//   localizeUrl('/ar/musalsal/the-boys', 'ru')
//     => '/ru/serial/the-boys'

import {APP_LOCALES, type AppLocale, DEFAULT_LOCALE, isAppLocale} from './locale-config';
import {reverseMap, TR_TO_TARGET} from './route-translations';

export interface LocalizeUrlOptions {
  /**
   * Explicit current locale override. When omitted, the locale is detected
   * from the URL's first segment (or DEFAULT_LOCALE if none matches).
   */
  currentLocale?: AppLocale;
}

export function localizeUrl(
  url: string,
  targetLocale: AppLocale,
  options: LocalizeUrlOptions = {},
): string {
  if (!APP_LOCALES.includes(targetLocale)) {
    throw new Error(`Unknown locale: ${targetLocale}. Available: ${APP_LOCALES.join(',')}`);
  }

  // Preserve original input shape: absolute URLs come back absolute;
  // relative ones come back relative.
  const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(url);

  let scheme = 'https';
  let host = '';
  let path = url;
  let query = '';
  let fragment = '';

  if (isAbsolute) {
    try {
      const parsed = new URL(url);
      scheme = parsed.protocol.replace(/:$/, '');
      host = parsed.host;
      path = parsed.pathname || '/';
      query = parsed.search;       // includes leading '?'
      fragment = parsed.hash;      // includes leading '#'
    } catch {
      // Fall through to relative-path treatment
    }
  } else {
    const fIdx = url.indexOf('#');
    if (fIdx !== -1) {
      fragment = url.slice(fIdx);
      path = url.slice(0, fIdx);
    }
    const qIdx = path.indexOf('?');
    if (qIdx !== -1) {
      query = path.slice(qIdx);
      path = path.slice(0, qIdx);
    }
  }

  // Split path into non-empty segments
  let segments = path.split('/').filter(s => s !== '');

  // Detect current locale from first segment (fallback to options.currentLocale → DEFAULT_LOCALE)
  let currentLocale: AppLocale = options.currentLocale ?? DEFAULT_LOCALE;
  if (segments.length > 0 && isAppLocale(segments[0])) {
    currentLocale = segments[0] as AppLocale;
    segments = segments.slice(1);
  } else if (options.currentLocale) {
    currentLocale = options.currentLocale;
  }

  if (targetLocale !== currentLocale) {
    // Step 1: if current != tr, reverse to TR
    if (currentLocale !== 'tr') {
      const reverse = reverseMap(currentLocale as Exclude<AppLocale, 'tr'>);
      segments = segments.map(s => reverse[s] ?? s);
    }

    // Step 2: if target != tr, forward TR -> target
    if (targetLocale !== 'tr') {
      const forward = TR_TO_TARGET[targetLocale as Exclude<AppLocale, 'tr'>];
      segments = segments.map(s => forward[s] ?? s);
    }
  }

  const newPath = '/' + targetLocale + (segments.length > 0 ? '/' + segments.join('/') : '');

  if (isAbsolute) {
    return `${scheme}://${host}${newPath}${query}${fragment}`;
  }
  return `${newPath}${query}${fragment}`;
}
